import { useCallback, useMemo } from "react";
import { Hostname } from "../../../types";

interface VerificationResult {
  isValid: boolean;
  matchedHostname: Hostname | null;
  normalizedInput: string | null;
  error: string | null;
}

const EMPTY_HOSTNAMES: Hostname[] = [];

/**
 * Normalizes a URL/domain string for comparison
 * - Removes protocol (http://, https://, etc.)
 * - Removes www. prefix
 * - Extracts hostname from full URLs
 * - Converts to lowercase
 */
const normalizeDomain = (input: string): string | null => {
  if (!input || typeof input !== 'string') return null;

  let domain = input.trim().toLowerCase();

  // Remove protocol
  domain = domain.replace(/^https?:\/\//, '');
  domain = domain.replace(/^ftp:\/\//, '');
  domain = domain.replace(/^\/\//, '');

  // Remove path, query, and hash
  domain = domain.split('/')[0];
  domain = domain.split('?')[0];
  domain = domain.split('#')[0];

  // Remove port
  domain = domain.split(':')[0];

  // Remove www. prefix
  domain = domain.replace(/^www\./, '');

  return domain || null;
};

/**
 * Checks if a domain matches a hostname entry
 * Handles exact matches and subdomain relationships
 */
const isDomainMatch = (inputDomain: string, hostname: string): boolean => {
  const normalizedInput = normalizeDomain(inputDomain);
  const normalizedHostname = normalizeDomain(hostname);

  if (!normalizedInput || !normalizedHostname) return false;

  // Exact match only (subdomains are treated separately)
  return normalizedInput === normalizedHostname;
};

/**
 * Custom hook to verify domains against a list of hostnames
 * @param hostnames - Array of hostname objects with hostname and verified properties
 * @returns Object with verifyDomain and isVerified functions
 */
export const useDomainVerifier = (hostnames: Hostname[] = EMPTY_HOSTNAMES) => {

  /**
   * Verifies if a given input (URL/domain/string) matches any verified hostname
   * @param input - URL, domain, or string to verify
   * @returns Verification result with details
   */
  const verifyDomain = useCallback((input: string): VerificationResult => {
    const normalizedInput = normalizeDomain(input);

    if (!normalizedInput) {
      return {
        isValid: false,
        matchedHostname: null,
        normalizedInput: null,
        error: 'Invalid input'
      };
    }

    // Find matching hostname that is verified
    const matchedHostname = hostnames.find(
      (host) => host.verified && isDomainMatch(input, host.hostname)
    );

    return {
      isValid: !!matchedHostname,
      matchedHostname: matchedHostname || null,
      normalizedInput,
      error: null
    };
  }, [hostnames]);

  /**
   * Quick check if domain is verified
   * @param input - URL, domain, or string to verify
   * @returns Boolean indicating if domain is verified
   */
  const isVerified = useCallback((input: string): boolean => {
    return verifyDomain(input).isValid;
  }, [verifyDomain]);

  return useMemo(() => ({ verifyDomain, isVerified }), [isVerified, verifyDomain]);
};
